import { Centrifuge } from "centrifuge";
import { api } from "@/services/Api";

/**
 * Client temps réel Centrifugo pour la Console Chat.
 * Récupère un token de connexion auprès du backend (wichat /conversation/centrifugo-token),
 * se connecte au hub (VUE_APP_CENTRIFUGO_URL) et expose subscribe/unsubscribe.
 * Dégradé silencieux si non configuré -> la console retombe sur le delta polling.
 */
class CentrifugoManager {
  private client: any = null;
  // Handlers par canal (PLUSIEURS abonnés possibles : la console ET la bannière
  // d'escalade écoutent `chat:agents`). Les canaux sont souscrits CÔTÉ SERVEUR
  // (claim `channels` du token) -> les publications arrivent via l'event
  // `publication` du client, qu'on route ici à tous les handlers du canal. Pas
  // de subscribe côté client (évite la dépendance à allow_subscribe_for_client).
  private handlers: Record<string, Array<(data: any) => void>> = {};
  // Connexion temps réel effectivement établie. Sert aux consommateurs pour ne
  // déclencher leur polling XHR de fallback QUE quand le temps réel est HS.
  private connected = false;

  async connect(): Promise<boolean> {
    if (this.client) return true;
    let url = (process.env.VUE_APP_CENTRIFUGO_URL || "").trim();
    // Page servie en HTTPS (tunnel Cloudflare) : un websocket en ws:// est refuse
    // par le navigateur au meme titre qu'un appel http://. On passe par le proxy
    // du serveur de dev, qui relaie le websocket sur la meme origine.
    if (typeof window !== "undefined" && window.location.protocol === "https:" && url.startsWith("ws://")) {
      url = "wss://" + window.location.host + "/_svc/centrifugo/connection/websocket";
    }
    if (!url) {
      console.warn("[centrifugo] VUE_APP_CENTRIFUGO_URL absent -> temps réel via polling uniquement");
      return false;
    }

    let token = "";
    try {
      const res = await api.get(api.chat, "conversation/centrifugo-token");
      token = res?.data?.token || "";
    } catch (e) {
      console.warn("[centrifugo] token introuvable", e);
      return false;
    }
    if (!token) return false;

    try {
      this.client = new Centrifuge(url, { token });
      this.client.on("connected", (ctx: any) => { this.connected = true; console.info("[centrifugo] connecté", ctx); });
      this.client.on("connecting", (ctx: any) => { this.connected = false; console.info("[centrifugo] connexion…", ctx); });
      this.client.on("disconnected", (ctx: any) => { this.connected = false; console.warn("[centrifugo] déconnecté", ctx); });
      this.client.on("error", (ctx: any) => console.warn("[centrifugo] erreur", ctx));
      // Publications des souscriptions server-side (canaux du token JWT).
      this.client.on("publication", (ctx: any) => {
        (this.handlers[ctx.channel] || []).forEach((h) => {
          try { h(ctx.data); } catch (e) { /* un handler ne doit pas casser les autres */ }
        });
      });
      this.client.connect();
      return true;
    } catch (e) {
      console.warn("[centrifugo] échec connexion", e);
      this.client = null;
      return false;
    }
  }

  /** True si la connexion websocket est établie -> le polling de fallback peut se taire. */
  isHealthy(): boolean {
    return !!this.client && this.connected;
  }

  subscribe(channel: string, onData: (data: any) => void): void {
    // Souscription server-side : on enregistre le handler (la connexion est déjà
    // abonnée au canal via le token). Plusieurs handlers par canal possibles.
    if (!this.handlers[channel]) this.handlers[channel] = [];
    if (!this.handlers[channel].includes(onData)) this.handlers[channel].push(onData);
  }

  /** Retire un handler précis (si fourni) ou tous les handlers du canal. */
  unsubscribe(channel: string, onData?: (data: any) => void): void {
    if (!this.handlers[channel]) return;
    if (onData) {
      this.handlers[channel] = this.handlers[channel].filter((h) => h !== onData);
    } else {
      delete this.handlers[channel];
    }
  }

  disconnect(): void {
    this.handlers = {};
    this.connected = false;
    if (this.client) {
      try {
        this.client.disconnect();
      } catch (e) {
        /* ignore */
      }
      this.client = null;
    }
  }
}

export const centrifugoManager = new CentrifugoManager();
